import { useMemo } from "react";

import { useGetAnswersQuery } from "@/redux/service";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";
import { UseExistingAnswerIds } from "@/features/survey-views/form/types";
import { toAnswerIds } from "@/features/survey-views/form/utils";

/**
 * The ids of the answers that exist right now for this survey and organization.
 */
export const useExistingAnswerIds = (
  surveyId?: number,
): UseExistingAnswerIds => {
  const organizationId = useOrganizationId();
  const isSkipped = !surveyId || !organizationId;

  const answersResponse = useGetAnswersQuery(
    {
      survey: surveyId,
      organization: organizationId,
      pagination: "off",
      mode: "base",
    },
    { skip: isSkipped },
  );

  const existingAnswerIds = useMemo(() => {
    if (!answersResponse.isSuccess) return null;
    return toAnswerIds(answersResponse.data);
  }, [answersResponse.isSuccess, answersResponse.data]);

  return {
    existingAnswerIds,
    isReady: isSkipped || answersResponse.isSuccess || answersResponse.isError,
    refetchAnswerIds: async () => {
      if (isSkipped) return null;
      return toAnswerIds(await answersResponse.refetch().unwrap());
    },
  };
};
